import type { Country } from './types';

/**
 * North American countries with full metadata.
 */
export const northAmericaCountries: Country[] = [
  {
    code: 'US',
    name: 'United States',
    officialName: 'United States of America',
    flag: '\u{1F1FA}\u{1F1F8}',
    continent: 'North America',
    subregion: 'Northern America',
    details: {
      capital: 'Washington, D.C.',
      population: '331.9M',
      currency: 'USD ($)',
      languages: 'English',
      area: '9,833,520 km\u00B2',
    },
    mapHints: {
      nameInAtlas: 'United States of America',
      // US has Alaska (far), Hawaii (far), and territories (Puerto Rico, Guam, etc.)
      // Show contiguous + Alaska + Hawaii, exclude very distant territories
      fitStrategy: 'auto',
      maxTerritoryDistance: 5000, // Include Hawaii (~4000km) and Alaska
      minAreaRatio: 0.05, // Include Alaska (large)
    },
  },
  {
    code: 'CA',
    name: 'Canada',
    flag: '\u{1F1E8}\u{1F1E6}',
    continent: 'North America',
    subregion: 'Northern America',
    details: {
      capital: 'Ottawa',
      population: '38.2M',
      currency: 'CAD ($)',
      languages: 'English, French',
      area: '9,984,670 km\u00B2',
    },
  },
  {
    code: 'MX',
    name: 'Mexico',
    flag: '\u{1F1F2}\u{1F1FD}',
    continent: 'North America',
    subregion: 'Central America',
    details: {
      capital: 'Mexico City',
      population: '130.3M',
      currency: 'MXN ($)',
      languages: 'Spanish',
      area: '1,964,375 km\u00B2',
    },
  },
  {
    code: 'GT',
    name: 'Guatemala',
    flag: '\u{1F1EC}\u{1F1F9}',
    continent: 'North America',
    subregion: 'Central America',
    details: {
      capital: 'Guatemala City',
      population: '17.4M',
      currency: 'GTQ (Q)',
      languages: 'Spanish',
      area: '108,889 km\u00B2',
    },
  },
  {
    code: 'BZ',
    name: 'Belize',
    flag: '\u{1F1E7}\u{1F1FF}',
    continent: 'North America',
    subregion: 'Central America',
    details: {
      capital: 'Belmopan',
      population: '405K',
      currency: 'BZD ($)',
      languages: 'English',
      area: '22,966 km\u00B2',
    },
  },
  {
    code: 'HN',
    name: 'Honduras',
    flag: '\u{1F1ED}\u{1F1F3}',
    continent: 'North America',
    subregion: 'Central America',
    details: {
      capital: 'Tegucigalpa',
      population: '10.1M',
      currency: 'HNL (L)',
      languages: 'Spanish',
      area: '112,492 km\u00B2',
    },
  },
  {
    code: 'SV',
    name: 'El Salvador',
    flag: '\u{1F1F8}\u{1F1FB}',
    continent: 'North America',
    subregion: 'Central America',
    details: {
      capital: 'San Salvador',
      population: '6.5M',
      currency: 'USD ($)',
      languages: 'Spanish',
      area: '21,041 km\u00B2',
    },
  },
  {
    code: 'NI',
    name: 'Nicaragua',
    flag: '\u{1F1F3}\u{1F1EE}',
    continent: 'North America',
    subregion: 'Central America',
    details: {
      capital: 'Managua',
      population: '6.9M',
      currency: 'NIO (C$)',
      languages: 'Spanish',
      area: '130,373 km\u00B2',
    },
  },
  {
    code: 'CR',
    name: 'Costa Rica',
    flag: '\u{1F1E8}\u{1F1F7}',
    continent: 'North America',
    subregion: 'Central America',
    details: {
      capital: 'San Jos\u00E9',
      population: '5.2M',
      currency: 'CRC (\u20A1)',
      languages: 'Spanish',
      area: '51,100 km\u00B2',
    },
  },
  {
    code: 'PA',
    name: 'Panama',
    flag: '\u{1F1F5}\u{1F1E6}',
    continent: 'North America',
    subregion: 'Central America',
    details: {
      capital: 'Panama City',
      population: '4.4M',
      currency: 'PAB (B/.), USD ($)',
      languages: 'Spanish',
      area: '75,417 km\u00B2',
    },
  },
  {
    code: 'CU',
    name: 'Cuba',
    flag: '\u{1F1E8}\u{1F1FA}',
    continent: 'North America',
    subregion: 'Caribbean',
    details: {
      capital: 'Havana',
      population: '11.3M',
      currency: 'CUP ($)',
      languages: 'Spanish',
      area: '109,884 km\u00B2',
    },
  },
  {
    code: 'JM',
    name: 'Jamaica',
    flag: '\u{1F1EF}\u{1F1F2}',
    continent: 'North America',
    subregion: 'Caribbean',
    details: {
      capital: 'Kingston',
      population: '3.0M',
      currency: 'JMD ($)',
      languages: 'English',
      area: '10,991 km\u00B2',
    },
  },
  {
    code: 'HT',
    name: 'Haiti',
    flag: '\u{1F1ED}\u{1F1F9}',
    continent: 'North America',
    subregion: 'Caribbean',
    details: {
      capital: 'Port-au-Prince',
      population: '11.6M',
      currency: 'HTG (G)',
      languages: 'French, Haitian Creole',
      area: '27,750 km\u00B2',
    },
  },
  {
    code: 'DO',
    name: 'Dominican Republic',
    flag: '\u{1F1E9}\u{1F1F4}',
    continent: 'North America',
    subregion: 'Caribbean',
    details: {
      capital: 'Santo Domingo',
      population: '11.1M',
      currency: 'DOP ($)',
      languages: 'Spanish',
      area: '48,671 km\u00B2',
    },
    mapHints: {
      nameInAtlas: 'Dominican Rep.',
    },
  },
  {
    code: 'PR',
    name: 'Puerto Rico',
    flag: '\u{1F1F5}\u{1F1F7}',
    continent: 'North America',
    subregion: 'Caribbean',
    details: {
      capital: 'San Juan',
      population: '3.2M',
      currency: 'USD ($)',
      languages: 'Spanish, English',
      area: '9,104 km\u00B2',
    },
  },
  {
    code: 'TT',
    name: 'Trinidad and Tobago',
    flag: '\u{1F1F9}\u{1F1F9}',
    continent: 'North America',
    subregion: 'Caribbean',
    details: {
      capital: 'Port of Spain',
      population: '1.4M',
      currency: 'TTD ($)',
      languages: 'English',
      area: '5,131 km\u00B2',
    },
  },
  {
    code: 'BS',
    name: 'Bahamas',
    flag: '\u{1F1E7}\u{1F1F8}',
    continent: 'North America',
    subregion: 'Caribbean',
    details: {
      capital: 'Nassau',
      population: '407K',
      currency: 'BSD ($)',
      languages: 'English',
      area: '13,943 km\u00B2',
    },
  },
  {
    code: 'BB',
    name: 'Barbados',
    flag: '\u{1F1E7}\u{1F1E7}',
    continent: 'North America',
    subregion: 'Caribbean',
    details: {
      capital: 'Bridgetown',
      population: '288K',
      currency: 'BBD ($)',
      languages: 'English',
      area: '430 km\u00B2',
    },
  },
  {
    code: 'AG',
    name: 'Antigua and Barbuda',
    flag: '\u{1F1E6}\u{1F1EC}',
    continent: 'North America',
    subregion: 'Caribbean',
    details: {
      capital: "Saint John's",
      population: '104K',
      currency: 'XCD ($)',
      languages: 'English',
      area: '442 km\u00B2',
    },
    mapHints: {
      nameInAtlas: 'Antigua and Barb.',
    },
  },
  {
    code: 'DM',
    name: 'Dominica',
    flag: '\u{1F1E9}\u{1F1F2}',
    continent: 'North America',
    subregion: 'Caribbean',
    details: {
      capital: 'Roseau',
      population: '67K',
      currency: 'XCD ($)',
      languages: 'English',
      area: '751 km\u00B2',
    },
  },
  {
    code: 'GD',
    name: 'Grenada',
    flag: '\u{1F1EC}\u{1F1E9}',
    continent: 'North America',
    subregion: 'Caribbean',
    details: {
      capital: "St. George's",
      population: '109K',
      currency: 'XCD ($)',
      languages: 'English',
      area: '344 km\u00B2',
    },
  },
  {
    code: 'KN',
    name: 'Saint Kitts and Nevis',
    flag: '\u{1F1F0}\u{1F1F3}',
    continent: 'North America',
    subregion: 'Caribbean',
    details: {
      capital: 'Basseterre',
      population: '51K',
      currency: 'XCD ($)',
      languages: 'English',
      area: '261 km\u00B2',
    },
  },
  {
    code: 'LC',
    name: 'Saint Lucia',
    flag: '\u{1F1F1}\u{1F1E8}',
    continent: 'North America',
    subregion: 'Caribbean',
    details: {
      capital: 'Castries',
      population: '184K',
      currency: 'XCD ($)',
      languages: 'English',
      area: '616 km\u00B2',
    },
  },
  {
    code: 'VC',
    name: 'Saint Vincent and the Grenadines',
    flag: '\u{1F1FB}\u{1F1E8}',
    continent: 'North America',
    subregion: 'Caribbean',
    details: {
      capital: 'Kingstown',
      population: '111K',
      currency: 'XCD ($)',
      languages: 'English',
      area: '389 km\u00B2',
    },
  },
];

/**
 * South American countries with full metadata.
 */
export const southAmericaCountries: Country[] = [
  {
    code: 'BR',
    name: 'Brazil',
    flag: '\u{1F1E7}\u{1F1F7}',
    continent: 'South America',
    subregion: 'South America',
    details: {
      capital: 'Bras\u00EDlia',
      population: '214.0M',
      currency: 'BRL (R$)',
      languages: 'Portuguese',
      area: '8,515,767 km\u00B2',
    },
  },
  {
    code: 'AR',
    name: 'Argentina',
    flag: '\u{1F1E6}\u{1F1F7}',
    continent: 'South America',
    subregion: 'South America',
    details: {
      capital: 'Buenos Aires',
      population: '45.4M',
      currency: 'ARS ($)',
      languages: 'Spanish',
      area: '2,780,400 km\u00B2',
    },
  },
  {
    code: 'CL',
    name: 'Chile',
    flag: '\u{1F1E8}\u{1F1F1}',
    continent: 'South America',
    subregion: 'South America',
    details: {
      capital: 'Santiago',
      population: '19.5M',
      currency: 'CLP ($)',
      languages: 'Spanish',
      area: '756,102 km\u00B2',
    },
    mapHints: {
      // Chile has Easter Island (very far) - exclude it
      fitStrategy: 'auto',
      maxTerritoryDistance: 1500,
    },
  },
  {
    code: 'CO',
    name: 'Colombia',
    flag: '\u{1F1E8}\u{1F1F4}',
    continent: 'South America',
    subregion: 'South America',
    details: {
      capital: 'Bogot\u00E1',
      population: '51.5M',
      currency: 'COP ($)',
      languages: 'Spanish',
      area: '1,141,748 km\u00B2',
    },
  },
  {
    code: 'PE',
    name: 'Peru',
    flag: '\u{1F1F5}\u{1F1EA}',
    continent: 'South America',
    subregion: 'South America',
    details: {
      capital: 'Lima',
      population: '33.4M',
      currency: 'PEN (S/)',
      languages: 'Spanish',
      area: '1,285,216 km\u00B2',
    },
  },
  {
    code: 'VE',
    name: 'Venezuela',
    flag: '\u{1F1FB}\u{1F1EA}',
    continent: 'South America',
    subregion: 'South America',
    details: {
      capital: 'Caracas',
      population: '28.4M',
      currency: 'VES (Bs.)',
      languages: 'Spanish',
      area: '916,445 km\u00B2',
    },
  },
  {
    code: 'EC',
    name: 'Ecuador',
    flag: '\u{1F1EA}\u{1F1E8}',
    continent: 'South America',
    subregion: 'South America',
    details: {
      capital: 'Quito',
      population: '18.0M',
      currency: 'USD ($)',
      languages: 'Spanish',
      area: '276,841 km\u00B2',
    },
    mapHints: {
      // Ecuador has Galapagos Islands (~1000km away) - include them
      fitStrategy: 'auto',
      maxTerritoryDistance: 1200,
    },
  },
  {
    code: 'BO',
    name: 'Bolivia',
    flag: '\u{1F1E7}\u{1F1F4}',
    continent: 'South America',
    subregion: 'South America',
    details: {
      capital: 'Sucre',
      population: '12.0M',
      currency: 'BOB (Bs)',
      languages: 'Spanish',
      area: '1,098,581 km\u00B2',
    },
  },
  {
    code: 'PY',
    name: 'Paraguay',
    flag: '\u{1F1F5}\u{1F1FE}',
    continent: 'South America',
    subregion: 'South America',
    details: {
      capital: 'Asunci\u00F3n',
      population: '7.2M',
      currency: 'PYG (\u20B2)',
      languages: 'Spanish, Guaran\u00ED',
      area: '406,752 km\u00B2',
    },
  },
  {
    code: 'UY',
    name: 'Uruguay',
    flag: '\u{1F1FA}\u{1F1FE}',
    continent: 'South America',
    subregion: 'South America',
    details: {
      capital: 'Montevideo',
      population: '3.5M',
      currency: 'UYU ($)',
      languages: 'Spanish',
      area: '176,215 km\u00B2',
    },
  },
  {
    code: 'GY',
    name: 'Guyana',
    flag: '\u{1F1EC}\u{1F1FE}',
    continent: 'South America',
    subregion: 'South America',
    details: {
      capital: 'Georgetown',
      population: '790K',
      currency: 'GYD ($)',
      languages: 'English',
      area: '214,969 km\u00B2',
    },
  },
  {
    code: 'SR',
    name: 'Suriname',
    flag: '\u{1F1F8}\u{1F1F7}',
    continent: 'South America',
    subregion: 'South America',
    details: {
      capital: 'Paramaribo',
      population: '612K',
      currency: 'SRD ($)',
      languages: 'Dutch',
      area: '163,820 km\u00B2',
    },
  },
];

/**
 * All Americas countries combined.
 */
export const americasCountries: Country[] = [
  ...northAmericaCountries,
  ...southAmericaCountries,
];
